import { useState } from 'react';
import {
  Button,
  DescriptionList,
  DescriptionListDetails,
  DescriptionListTerm,
  Panel,
  PanelActions,
  PanelFooter,
  PanelHeader,
  PanelSection,
  PanelTitle,
} from '@customerio/pluma-components/react';
import { WAREHOUSE_DESTINATION_CONFIG_KEYS } from '@/services/db/warehouse-sync/entity';
import type { WarehouseDestination, WarehouseSync } from '@/services/db/warehouse-sync/entity';
import type { BucketConfigDraft } from '@/features/warehouse-sync';
import { ManageConfiguration } from './manage-configuration';

type BucketConfigurationProps = {
  sync: WarehouseSync;
  destination: WarehouseDestination;
  isReadOnly: boolean;
  isUpdating: boolean;
  /** Reported when the modal opens, so the funnel has a start per completion. */
  onManageOpened: () => void;
  onUpdate: (config: BucketConfigDraft) => Promise<boolean>;
};

/** Azure identifies its container by path alone; the rest carry a bucket name. */
function bucketFields(sync: WarehouseSync, destination: WarehouseDestination) {
  const config = sync[WAREHOUSE_DESTINATION_CONFIG_KEYS[destination]];
  if (config == null) {
    return { bucketName: null, bucketPath: '' };
  }
  return {
    bucketName: 'bucket_name' in config ? config.bucket_name : null,
    bucketPath: config.bucket_path,
  };
}

/** The stored config, as the credential form's starting point. */
function storedConfig(sync: WarehouseSync, destination: WarehouseDestination): BucketConfigDraft {
  return sync[WAREHOUSE_DESTINATION_CONFIG_KEYS[destination]] ?? {};
}

/** The bucket this sync writes to, and the way to re-point or re-key it. */
export function BucketConfiguration({
  sync,
  destination,
  isReadOnly,
  isUpdating,
  onManageOpened,
  onUpdate,
}: BucketConfigurationProps) {
  const { bucketName, bucketPath } = bucketFields(sync, destination);
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <>
      <Panel data-test-warehouse-bucket-configuration>
        <PanelHeader>
          <PanelTitle level="3">Bucket configuration</PanelTitle>
        </PanelHeader>
        <PanelSection>
          <DescriptionList>
            {bucketName != null ? (
              <>
                <DescriptionListTerm color="subtle">Bucket name</DescriptionListTerm>
                <DescriptionListDetails data-test-warehouse-bucket-name>{bucketName}</DescriptionListDetails>
              </>
            ) : null}
            <DescriptionListTerm color="subtle">Bucket path</DescriptionListTerm>
            <DescriptionListDetails data-test-warehouse-bucket-path>{bucketPath}</DescriptionListDetails>
          </DescriptionList>
        </PanelSection>
        <PanelFooter>
          <PanelActions>
            {/* Ungated: a viewer can read the bucket settings, which the panel above
                already shows. The gate that matters is on the modal's submit. */}
            <Button
              data-test-warehouse-button="manage-configuration"
              variant="secondary"
              onClick={() => {
                onManageOpened();
                setIsModalOpen(true);
              }}
            >
              Manage configuration…
            </Button>
          </PanelActions>
        </PanelFooter>
      </Panel>

      {isModalOpen ? (
        <ManageConfiguration
          destination={destination}
          storedConfig={storedConfig(sync, destination)}
          isReadOnly={isReadOnly}
          isUpdating={isUpdating}
          onClose={() => setIsModalOpen(false)}
          onUpdate={onUpdate}
        />
      ) : null}
    </>
  );
}
